import type { HTMLAttributes, ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, OctagonAlert } from 'lucide-react';
import { clsx } from '../clsx';
import { Icon } from './Icon';
import styles from './Banner.module.css';

export type BannerTone = 'info' | 'warning' | 'danger' | 'neutral' | 'success' | 'accent';

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: BannerTone | undefined;
  withIcon?: boolean | undefined;
  /** Bold first line in the tone colour ("Möglicher Doppeleintrag: gleiche Telefonnummer"). */
  title?: ReactNode | undefined;
  /** Row of buttons/links under the text (8 px gap above). */
  actions?: ReactNode | undefined;
}

const icons = {
  info: Info,
  warning: AlertTriangle,
  danger: OctagonAlert,
  neutral: Info,
  success: CheckCircle2,
  accent: Info,
} as const;

/** Inline notice box of the screens: 12/14 padding, 12.5 px text, tone background; danger/warning are announced. */
export function Banner({
  tone = 'info',
  withIcon = false,
  title,
  actions,
  className,
  children,
  ...rest
}: BannerProps) {
  const role = tone === 'danger' || tone === 'warning' ? 'alert' : 'status';
  const Glyph = icons[tone];
  return (
    <div role={role} className={clsx(styles.banner, styles[tone], className)} {...rest}>
      {withIcon ? <Icon icon={Glyph} size={16} className={styles.icon} /> : null}
      <div className={styles.content}>
        {title ? <span className={styles.title}>{title}</span> : null}
        {children}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
    </div>
  );
}
